import {
  getEffectiveInferenceModelId,
  getVisibleInferenceModels,
  isAgeVerificationMandatory,
  useApp,
  useTranslatedLanguage,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { AgeVerificationStatus } from "@next-platform/packages-ts-types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AgeVerificationWarningModal } from "@/components";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import { useAgeVerificationFlags, useInferenceSettings } from "@/hooks";
import { ModalSelectModel } from "@/pages/Chat/components/ModalSelectModel";
import useAuth from "@/services/useAuth";
import { isAgeVerificationWarningModalNeeded } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import LanguageSelectionModal from "../LanguageSelectionModal/LanguageSelectionModal";
import GeneralSettingsItem from "./GeneralSettingsItem";
import { GENERAL_SEETING_TYPE, SettingsItemProps } from "./types";

enum MODAL_TYPE {
  LANGUAGE = "language",
  // VOICE = "voice", // may add in future leave it here for now
  MODEL = "model",
}
interface IGeneralSettingsSectionProps {
  handleBlurNsfwToggle: () => Promise<void>;
}

const GeneralSettingsSection = ({ handleBlurNsfwToggle }: IGeneralSettingsSectionProps) => {
  const { t } = useTranslation();
  const { getTranslatedLanguage } = useTranslatedLanguage();
  const { blurNSFW, showNSFW, userChatLanguageName: selectedLanguageName, onUserSetNSFWSettings } = useUserSettings();
  const { inferenceModel } = useInferenceSettings();
  const { userProfile } = useUserInfo();
  const [isAgeVerificationVisible, setIsAgeVerificationVisible] = useState<boolean | undefined>(undefined);
  const { inferenceModels } = useApp();
  const [stateIsLanguageSelectionVisible, setStateIsLanguageSelectionVisible] = useState(false);
  const [ageVerificationMode, setAgeVerificationMode] = useState<"nsfwtoggle" | undefined>(undefined);
  const [hasHandledAgeVerification, setHasHandledAgeVerification] = useState(false);
  const [stateIsModelSelectionVisible, setStateIsModelSelectionVisible] = useState(false);
  const navigate = useNavigate();
  const isPartner = getIsPartner();
  const { isAgeVerificationEnabled } = useAgeVerificationFlags();
  const isNsfwUserToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  const isChangeModelAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatGenerationChangeModel);
  const isChatMultilanguageAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatMultilanguage);
  const { permissions } = useAuth();
  const effectiveModelId = getEffectiveInferenceModelId(inferenceModels, permissions, inferenceModel);
  const canChangeModel =
    isChangeModelAllowed && getVisibleInferenceModels(inferenceModels, permissions).some((model) => model.id !== effectiveModelId);

  const handleLanguageModal = (visible: boolean) => {
    setStateIsLanguageSelectionVisible(visible);
  };

  const handleModalOpen = (type: MODAL_TYPE) => {
    switch (type) {
      case MODAL_TYPE.LANGUAGE:
        handleLanguageModal(true);
        break;
      // case MODAL_TYPE.VOICE: // may add in future leave it here for now
      //     console.log("open voice modal");
      //     break;
      case MODAL_TYPE.MODEL:
        setStateIsModelSelectionVisible(true);
        break;
      default:
        break;
    }
  };

  const nsfwToggleHanlder = () => {
    setAgeVerificationMode("nsfwtoggle");
    setHasHandledAgeVerification(false);

    const isWarningModalNeeded = isAgeVerificationWarningModalNeeded(userProfile?.age_verification?.status);
    if (!showNSFW && isAgeVerificationEnabled && isWarningModalNeeded) {
      setIsAgeVerificationVisible(true);
      return;
    }

    void onUserSetNSFWSettings(!showNSFW);
  };

  const onCloseAgeVerificationWarningModal = () => {
    const isMandatoryAgeVerification = isAgeVerificationMandatory(userProfile?.age_verification?.status);

    if (ageVerificationMode === "nsfwtoggle" && !hasHandledAgeVerification && !isMandatoryAgeVerification) {
      void onUserSetNSFWSettings(true);
      setHasHandledAgeVerification(true);
    }
    setAgeVerificationMode(undefined);
    setIsAgeVerificationVisible(false);
  };

  const settingsConfig = [
    {
      title: t("user:profile.settings.displayExplicitContent.title"),
      subtitle: t("user:profile.settings.displayExplicitContent.subtitle"),
      icon: IconVariants.FLAME,
      type: GENERAL_SEETING_TYPE.TOGGLE,
      value: showNSFW,
      onClick: nsfwToggleHanlder,
      hidden: !isNsfwUserToggleAllowed,
    },
    {
      title: t("user:profile.settings.blurExplicitImages.title"),
      subtitle: t("user:profile.settings.blurExplicitImages.subtitle"),
      icon: IconVariants.EYE_OFF,
      type: GENERAL_SEETING_TYPE.TOGGLE,
      value: blurNSFW,
      onClick: () => {
        void handleBlurNsfwToggle();
      },
      hidden: !isNsfwUserToggleAllowed,
      disabled: !showNSFW,
    },
    {
      title: t("user:profile.settings.defaultModel.title"),
      subtitle: canChangeModel ? t("user:profile.settings.defaultModel.subtitle") : undefined,
      icon: IconVariants.SPARKLES,
      type: canChangeModel ? GENERAL_SEETING_TYPE.MODAL : GENERAL_SEETING_TYPE.TEXT,
      value: inferenceModels?.find((model) => model.id === effectiveModelId)?.name,
      onClick: canChangeModel
        ? () => {
            handleModalOpen(MODAL_TYPE.MODEL);
          }
        : undefined,
      hidden: !isChangeModelAllowed,
    },
    {
      title: t("user:profile.settings.preferredChatLanguage.title"),
      subtitle: t("user:profile.settings.preferredChatLanguage.subtitle"),
      icon: IconVariants.LANGUAGES,
      type: GENERAL_SEETING_TYPE.MODAL,
      value: getTranslatedLanguage(selectedLanguageName),
      onClick: () => {
        handleModalOpen(MODAL_TYPE.LANGUAGE);
      },
      hidden: isPartner || !isChatMultilanguageAllowed,
    },
    // may add in future leave it here for now
    // {
    //     key: "defaultVoice",
    //     title: "Default Voice",
    //     subtitle: "Chat with talking",
    //     type: GENERAL_SEETING_TYPE.MODAL,
    //     value: "Classy British Man",
    //     onOpen: () => { handleModalOpen(MODAL_TYPE.VOICE) },
    // },
  ] as SettingsItemProps[];

  if (isAgeVerificationEnabled && userProfile?.age_verification?.status && userProfile?.age_verification?.status !== AgeVerificationStatus.DISABLED) {
    settingsConfig.unshift({
      title: t("user:profile.settings.ageVerification.title"),
      subtitle: t("user:profile.settings.ageVerification.subtitle"),
      type: GENERAL_SEETING_TYPE.BUTTON,
      icon: IconVariants.SHIELD_CHECK,
      disabled: userProfile?.age_verification?.status === AgeVerificationStatus.VERIFIED,
      value:
        userProfile?.age_verification?.status === AgeVerificationStatus.VERIFIED
          ? t("user:profile.settings.ageVerification.verified")
          : t("user:profile.settings.ageVerification.verify"),
      onClick: () => {
        navigate(ROUTES.ageVerification);
      },
    });
  }

  const visibleSettings = settingsConfig.filter((setting) => !setting.hidden);
  if (visibleSettings.length === 0) {
    return null;
  }

  return (
    <div className="py-lg">
      <Text variant={TextVariants.HEADING_6_MEDIUM}>
        <Trans i18nKey="user:profile.section.general" />
      </Text>
      {visibleSettings.map((setting) => (
        <GeneralSettingsItem key={setting.title} {...setting} />
      ))}
      {isChatMultilanguageAllowed && stateIsLanguageSelectionVisible ? (
        <LanguageSelectionModal closeModal={handleLanguageModal} dataTestId="SiteMultilanguageCapabilityGate" />
      ) : null}
      {canChangeModel ? (
        <ModalSelectModel isOpen={stateIsModelSelectionVisible} setIsOpen={setStateIsModelSelectionVisible} currentModelId={effectiveModelId} />
      ) : null}
      <AgeVerificationWarningModal showNSFW={showNSFW} isOpen={isAgeVerificationVisible} onClose={onCloseAgeVerificationWarningModal} />
    </div>
  );
};

export default GeneralSettingsSection;
